/**
 * useFilterVisibility — CR-4802: which filter controls this tenant shows.
 *
 * One answer for every panel, resolved by the backend from the tenant's
 * declared Admin Setup toggles (Platform tab → Filter Visibility) and the
 * detected defaults, served through `FilterVisibilityContext`. A panel gates
 * each control on the matching flag and nothing else:
 *
 *   const fv = useFilterVisibility();
 *   {fv.warehouse && <WarehouseFilter … />}
 *   {fv.commodity && <CommodityGroupFilter … />}
 *
 * Signals: warehouse, division, supplierCode, supplierType, commodity,
 * historicalLive, companyCountry. All default to true until the backend
 * answers (`ready` false) and stay true if it never does — a blip must never
 * narrow a panel. Outside a provider the hook answers "everything visible".
 *
 * Order Type is deliberately absent: the column it filtered is dormant and
 * CR-7273 removes the control instead of gating it.
 *
 * @returns the seven visibility flags plus `ready`
 */
import { useFilterVisibilityContext, type FilterVisibilityFlags } from '../contexts/FilterVisibilityContext';

export interface FilterVisibilityState extends FilterVisibilityFlags {
  /** True once the backend has answered. */
  ready: boolean;
}

export function useFilterVisibility(): FilterVisibilityState {
  const { flags, ready } = useFilterVisibilityContext();
  return { ...flags, ready };
}
